import { Routes, Route, Navigate } from "react-router-dom";
import { useAuth } from "./hooks/useAuth";
import AppShell from "./components/layout/AppShell";
import Login from "./pages/Login";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import AcceptInvite from "./pages/AcceptInvite";
import Dashboard from "./pages/Dashboard";
import Campers from "./pages/Campers";
import CamperDetail from "./pages/CamperDetail";
import Activities from "./pages/Activities";
import ActivityDetail from "./pages/ActivityDetail";
import ActivityProgress from "./pages/ActivityProgress";
import Cabins from "./pages/Cabins";
import KerchiefView from "./pages/reports/KerchiefView";
import KerchiefCeremonyReport from "./pages/reports/KerchiefCeremonyReport";
import AcBookReport from "./pages/reports/AcBookReport";
import KerchiefStampingReport from "./pages/reports/KerchiefStampingReport";
import CamperSummaryReport from "./pages/reports/CamperSummaryReport";
import RankingsPrint from "./pages/reports/RankingsPrint";
import AcBookEntry from "./pages/AcBookEntry";
import Reports from "./pages/Reports";
import AdminDashboard from "./pages/admin/AdminDashboard";
import ManageActivities from "./pages/admin/ManageActivities";
import KerchiefLayout from "./pages/admin/KerchiefLayout";
import ManageStaff from "./pages/admin/ManageStaff";
import ManageCabins from "./pages/admin/ManageCabins";
import ManageConfig from "./pages/admin/ManageConfig";
import ManageTerms from "./pages/admin/ManageTerms";
import ManageImports from "./pages/admin/ManageImports";
import DeveloperTools from "./pages/admin/DeveloperTools";
import ManageACs from "./pages/admin/ManageACs";
import ManageCampers from "./pages/admin/ManageCampers";
import CamperAcEntry from "./pages/admin/CamperAcEntry";
import TeamAssignment from "./pages/admin/TeamAssignment";
import TrendReporting from "./pages/admin/TrendReporting";

function RequireAuth({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-4 border-forest-500 border-t-transparent rounded-full animate-spin" />
          <p className="text-stone-500 text-sm">Loading ACTS…</p>
        </div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

function RequireAdmin({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  if (user?.role !== "admin" && user?.role !== "director") {
    return <Navigate to="/dashboard" replace />;
  }
  return <>{children}</>;
}

export default function App() {
  return (
    <Routes>
      {/* Public */}
      <Route path="/login" element={<Login />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/accept-invite" element={<AcceptInvite />} />

      {/* Authenticated */}
      <Route
        path="/"
        element={
          <RequireAuth>
            <AppShell />
          </RequireAuth>
        }
      >
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="campers" element={<Campers />} />
        <Route path="campers/:id" element={<CamperDetail />} />
        <Route path="activities" element={<Activities />} />
        <Route path="activities/:id" element={<ActivityDetail />} />
        <Route path="activities/:id/progress" element={<ActivityProgress />} />
        <Route path="cabins" element={<Cabins />} />
        <Route path="cabin-groups" element={<Navigate to="/cabins" replace />} />
        <Route path="reports" element={<Reports />} />
        <Route path="reports/kerchief" element={<KerchiefView />} />
        <Route path="reports/kerchief-ceremony" element={<KerchiefCeremonyReport />} />
        <Route path="reports/ac-books" element={<AcBookReport />} />
        <Route path="reports/kerchief-stamping" element={<KerchiefStampingReport />} />
        <Route path="reports/camper-summary" element={<CamperSummaryReport />} />
        <Route path="reports/rankings/print" element={<RankingsPrint />} />
        <Route path="reports/ac-book-entry" element={<Navigate to="/admin/manage-acs/book-entry" replace />} />

        {/* Admin-only */}
        <Route
          path="admin"
          element={
            <RequireAdmin>
              <AdminDashboard />
            </RequireAdmin>
          }
        />
        <Route
          path="admin/activities"
          element={
            <RequireAdmin>
              <ManageActivities />
            </RequireAdmin>
          }
        />
        <Route
          path="admin/kerchief-layout"
          element={
            <RequireAdmin>
              <KerchiefLayout />
            </RequireAdmin>
          }
        />
        <Route
          path="admin/staff"
          element={
            <RequireAdmin>
              <ManageStaff />
            </RequireAdmin>
          }
        />
        <Route
          path="admin/cabins"
          element={
            <RequireAdmin>
              <ManageCabins />
            </RequireAdmin>
          }
        />
        <Route
          path="admin/config"
          element={
            <RequireAdmin>
              <ManageConfig />
            </RequireAdmin>
          }
        />
        <Route
          path="admin/terms"
          element={
            <RequireAdmin>
              <ManageTerms />
            </RequireAdmin>
          }
        />
        <Route
          path="admin/imports"
          element={
            <RequireAdmin>
              <ManageImports />
            </RequireAdmin>
          }
        />
        <Route
          path="admin/developer-tools"
          element={
            <RequireAdmin>
              <DeveloperTools />
            </RequireAdmin>
          }
        />
        <Route
          path="admin/manage-acs"
          element={
            <RequireAdmin>
              <ManageACs />
            </RequireAdmin>
          }
        />
        <Route
          path="admin/manage-campers"
          element={
            <RequireAdmin>
              <ManageCampers />
            </RequireAdmin>
          }
        />
        <Route
          path="admin/manage-acs/book-entry"
          element={
            <RequireAdmin>
              <AcBookEntry />
            </RequireAdmin>
          }
        />
        <Route
          path="admin/manage-acs/camper-entry"
          element={
            <RequireAdmin>
              <CamperAcEntry />
            </RequireAdmin>
          }
        />
        <Route
          path="admin/team-assignment"
          element={
            <RequireAdmin>
              <TeamAssignment />
            </RequireAdmin>
          }
        />
        <Route
          path="admin/trends"
          element={
            <RequireAdmin>
              <TrendReporting />
            </RequireAdmin>
          }
        />
      </Route>

      {/* Catch-all */}
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}
